import { fetchTokenMetadata } from "@/api/endpoints";
import {
	convertToHumanAmount,
	FLUXB_TOKEN,
	IKnownToken,
	IToken,
	ITokenAccount,
	ITokenPriceUSD,
	SOL_MINT,
	SOL_TOKEN,
	TokenDTO,
	USDC_TOKEN,
} from "@/shared";
import { defineStore } from "pinia";
import Vue, { computed, ref } from "vue";
import fetchWithAuth from "@/plugins/fetch";

export const useCacheStore = defineStore("cache", () => {
	// data
	const tokenList = ref<{ [mint: string]: IToken }>({
		[USDC_TOKEN.mint]: USDC_TOKEN,
		[SOL_TOKEN.mint]: SOL_TOKEN,
		[FLUXB_TOKEN.mint]: FLUXB_TOKEN,
	});
	const tokenPricesUSD = ref<{
		[mint: string]: ITokenPriceUSD;
	}>({
		[USDC_TOKEN.mint]: {
			mint: USDC_TOKEN.mint,
			priceUSD: 1,
			updateAt: new Date(),
		},
	});
	const userTokenAccounts = ref<{
		[mint: string]: ITokenAccount;
	}>({});
	const nameMap = ref<{ [mint: string]: any }>({});

	const listState = ref<boolean>(false);

	const loadings = ref<string[]>([]);

	const watchlist = ref<string[]>([]);

	// methods
	function init() {
		const jsonTokens = localStorage.getItem(`fluxbeam:tokens`);

		if (!jsonTokens) return;

		const localTokens = JSON.parse(jsonTokens) ?? {};
		tokenList.value = { ...tokenList.value, ...localTokens };

		// watchlist
		const jsonWatchlist = localStorage.getItem(`fluxbeam:watchlist`);

		if (!jsonTokens) return;

		const localWatchlist = JSON.parse(jsonWatchlist) ?? [];

		watchlist.value = [...watchlist.value, ...localWatchlist];
	}
	async function cacheToken(mint: string) {
		if (!mint) return

		fetchMintPriceUSD(mint);

		if (tokenList.value[mint] || loadings.value.includes(mint)) {
			console.log("cacheToken SKIP" ,mint)
			return;
		}

		let token = null
		try {
			loadings.value.push(mint);

			const primitive = await fetchTokenMetadata(mint);

			if (primitive.error) {
				return false;
			}

			token = TokenDTO(primitive);

			addTokenList(token);
		} finally {
			loadings.value.splice(loadings.value.indexOf(mint), 1);
		}
		return token
	}
	function addUserTokenAccount(account: ITokenAccount): void {
		const old = userTokenAccounts.value[account.mint]
		if(old && old.amount && !account.amount)
			account.amount = old.amount

		Vue.set(userTokenAccounts.value, account.mint, { ...account });
	}
	function setListState(state: boolean): void {
		listState.value = state;
	}
	function updateTokenBalance(
		mint: string,
		amount: number,
		decimals = 0
	): void {
		const oldVal = userTokenAccounts.value[mint] || { amount: 0, decimals: 0 };
		oldVal.amount = amount;

		if (decimals > 0) oldVal.decimals = decimals;

		Vue.set(userTokenAccounts.value, mint, { ...oldVal });
	}
	function addTokenList(token: IToken): void {
		Vue.set(tokenList.value, token.mint, { ...token, createdAt: Date.now() });

		if (Object.keys(tokenList.value).length > 300) {
			// Convert to array and sort by createdAt timestamp
			const sortedTokens = Object.values(tokenList.value)
				.filter((token) => token.createdAt)
				.sort((a, b) => a.createdAt - b.createdAt);

			// Remove the 5 oldest tokens
			const tokensToRemove = sortedTokens.slice(0, 5);
			tokensToRemove.forEach((token) => {
				Vue.delete(tokenList.value, token.mint);
			});
		}

		localStorage.setItem(
			`fluxbeam:tokens`,
			JSON.stringify(tokenList.value, (_, v) =>
				typeof v === "bigint" ? v.toString() : v
			)
		);
	}
	function addTokenPricesUSD(priceUSD: ITokenPriceUSD): void {
		Vue.set(tokenPricesUSD.value, priceUSD.mint, {
			...priceUSD,
			updateAt: new Date(),
		});
	}
	async function fetchMintPriceUSD(mint: string): Promise<void> {
		if (!mint)
			return

		if (loadings.value.includes(mint)) return;
		// @ts-ignore
		if (Object.hasOwn(tokenPricesUSD.value, mint)) return;

		try {
			const response = await fetchWithAuth(
				`https://data.fluxbeam.xyz/tokens/${mint}/price`
			);

			if (!response.ok) {
				if (response.status === 404) {
					// Mint doesn't exist, return without doing anything
					return;
				}
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.text();

			if (!data) return;

			const priceUSD = Number(data);

			addTokenPricesUSD({
				mint,
				priceUSD,
			});
		} catch (e) {
			console.error(`Error fetching price for mint ${mint}:`, e);
			return;
		}
	}

	function addTokenName(mint: string, name: string, type = "TOKEN"): void {
		Vue.set(nameMap.value, mint, {
			name: name,
			type: type,
		});
	}

	async function fetchNameMap(): Promise<void> {
		nameMap.value = await (
			await fetch("https://api.rugcheck.xyz/public/known_accounts.json").catch()
		).json();
	}

	function addWatchlist(mint: string) {
		watchlist.value.push(mint);

		localStorage.setItem("fluxbeam:watchlist", JSON.stringify(watchlist.value));
	}
	function removeWatchlist(mint: string) {
		watchlist.value.splice(watchlist.value.indexOf(mint), 1);

		localStorage.setItem("fluxbeam:watchlist", JSON.stringify(watchlist.value));
	}
	async function fetchTokenPricesUSD(mint: string): Promise<ITokenPriceUSD> {
		if (tokenPricesUSD.value[mint]) return tokenPricesUSD.value[mint];

		await fetchMintPriceUSD(mint);

		return (
			tokenPricesUSD.value[mint] || {
				mint: mint,
				priceUSD: 0,
				updateAt: new Date(),
			}
		);
	}

	// getters
	const getTokenName = computed<(mint: string) => IKnownToken>(() => (mint) => {
		return nameMap.value[mint];
	});
	const getTokenPricesUSD = computed<(mint: string) => ITokenPriceUSD>(
		() => (mint) => {
			return tokenPricesUSD.value[mint];
		}
	);
	const getUIAmountSOL = computed<number>(() => {
		const account = userTokenAccounts.value[SOL_MINT];
		if (!account) return 0;

		return convertToHumanAmount({
			amount: account.amount,
			decimals: SOL_TOKEN.decimals,
		});
	});
	const getAmountSOL = computed<number>(() => {
		const account = userTokenAccounts.value[SOL_MINT];
		if (!account) return 0;

		return account.amount;
	});
	const getTokenBalance = computed<(mint: string) => number>(() => (mint) => {
		const account = userTokenAccounts.value[mint];

		if (!account || !account?.decimals || !account.amount) return 0;

		return convertToHumanAmount({
			amount: account.amount,
			decimals: account.decimals,
		});
	});

	const getTokenBalanceUSD = computed<(mint: string) => number>(
		() => (mint) => {
			const account = userTokenAccounts.value[mint];
			const priceUSD = getTokenPricesUSD.value(mint);

			if (!account) return 0;
			if (!priceUSD) return 0;

			return (
				convertToHumanAmount({
					amount: account.amount,
					decimals: account.decimals,
				}) * priceUSD.priceUSD
			);
		}
	);

	const isInWatchlist = computed<(mint: string) => boolean>(
		() => (mint: string) => {
			return watchlist.value.includes(mint);
		}
	);

	// setup
	init();

	return {
		listState,
		tokenList,
		tokenPricesUSD,
		userTokenAccounts,
		addTokenList,
		addTokenPricesUSD,
		fetchTokenPricesUSD,
		addTokenName,
		addUserTokenAccount,
		updateTokenBalance,
		fetchMintPriceUSD,
		fetchNameMap,
		setListState,
		cacheToken,
		addWatchlist,
		removeWatchlist,
		getTokenPricesUSD,
		getUIAmountSOL,
		getAmountSOL,
		getTokenBalance,
		getTokenBalanceUSD,
		getTokenName,
		isInWatchlist
	};
});
